export class OPFSStorage {
    protected async getRoot(): Promise<FileSystemDirectoryHandle> {
        return navigator.storage.getDirectory();
    }

    protected async getDir(
        name: string,
        parent?: FileSystemDirectoryHandle,
    ): Promise<FileSystemDirectoryHandle> {
        const root = parent ?? (await this.getRoot());
        return root.getDirectoryHandle(name, { create: true });
    }

    protected async writeJson<T>(
        dir: FileSystemDirectoryHandle,
        filename: string,
        data: T,
    ): Promise<void> {
        const fileHandle = await dir.getFileHandle(filename, { create: true });
        const writable = await fileHandle.createWritable();
        try {
            await writable.write(JSON.stringify(data));
        } finally {
            await writable.close();
        }
    }

    protected async readJson<T>(
        dir: FileSystemDirectoryHandle,
        filename: string,
    ): Promise<T | null> {
        try {
            const fileHandle = await dir.getFileHandle(filename);
            const file = await fileHandle.getFile();
            return JSON.parse(await file.text()) as T;
        } catch {
            return null;
        }
    }

    protected async writeBinary(
        dir: FileSystemDirectoryHandle,
        filename: string,
        data: Blob | BufferSource | string,
    ): Promise<void> {
        const fileHandle = await dir.getFileHandle(filename, { create: true });
        const writable = await fileHandle.createWritable();
        try {
            await writable.write(data);
        } finally {
            await writable.close();
        }
    }

    protected async readBinary(
        dir: FileSystemDirectoryHandle,
        filename: string,
    ): Promise<File | null> {
        try {
            const fileHandle = await dir.getFileHandle(filename);
            return fileHandle.getFile();
        } catch {
            return null;
        }
    }

    protected async deleteFile(
        dir: FileSystemDirectoryHandle,
        filename: string,
    ): Promise<void> {
        try {
            await dir.removeEntry(filename);
        } catch {
            // ignore missing
        }
    }

    protected async listJsonEntries<T>(
        dir: FileSystemDirectoryHandle,
        ext = ".json",
    ): Promise<T[]> {
        const results: T[] = [];
        for await (const [name, handle] of dir.entries()) {
            if (handle.kind !== "file" || !name.endsWith(ext)) continue;
            try {
                const file = await (handle as FileSystemFileHandle).getFile();
                results.push(JSON.parse(await file.text()) as T);
            } catch {
                // corrupted — skip
            }
        }
        return results;
    }
}
